import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
  IndividualMemberDto,
  Gender,
  MembershipType,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
} from '../types/member';
import { memberService } from '../services/members';
import { changeRequestService } from '../services/changeRequests';
import { ChangeRequestStatus } from '../types/changeRequest';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Avatar } from '../components/common/Avatar';
import { VignetteStatusBadge } from '../components/admin/VignetteStatusBadge';
import ProfessionalProfileDisplay from '../components/profile/ProfessionalProfileDisplay';
import { resolveVignetteStatus } from '../utils/vignette';
import { tryNormalizeEnumValue } from '../utils/enumNormalize';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Container, Stack, Grid } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Text } from '../design-system/components/Typography';
import { Tabs, TabPanel } from '../design-system/patterns/Tabs';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { headerWatermarkStyles } from '../components/dashboard/dashboardWatermark';
import { MonumentCaption } from '../components/dashboard/MonumentCaption';
import { useMonumentWatermark } from '../hooks/useMonumentWatermark';
import { parseMemberCardNumber } from '../utils/memberCardNumber';

const ProfileContainer = styled(Container)`
  max-width: 1180px;
`;

const IdentityCard = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[2]};
  border-radius: ${borders.radius.lg};
  background: transparent;
  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const AccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const IdentityInner = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[5]};
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing[4]};
  }
`;

const IdentityMain = styled.div`
  display: flex;
  gap: ${spacing[4]};
  min-width: 0;
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;

  > *:first-child {
    flex-shrink: 0;
  }
`;

const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
  flex: 1;
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: clamp(1.4rem, 2.1vw, 1.9rem);
  font-weight: ${typography.fontWeight.semibold};
  line-height: 1.25;
  color: ${props => props.theme.colors.text};
  letter-spacing: ${typography.letterSpacing.tighter};
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: ${breakpoints.md}) {
    font-size: clamp(1.15rem, 5.2vw, 1.45rem);
  }
`;

const EditProfileButton = styled(Button)`
  flex-shrink: 0;

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]} ${spacing[4]};
  align-items: center;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${props => props.theme.colors.primary};
  }
`;

const SoftBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
  background: ${props => `${props.theme.colors.primary}12`};
  color: ${props => props.theme.colors.primary};
`;

const SectionCard = styled(Card)`
  padding: ${spacing[6]};
`;

const InfoGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
  gap: ${spacing[6]};
`;

const InfoItem = styled(Stack)`
  gap: ${spacing[2]};
`;

const EmptyValue = styled(Text)`
  font-style: italic;
  color: ${props => props.theme.colors.textMuted};
`;

const InfoLabel = styled(Text).attrs(() => ({
  size: 'xs' as const,
}))`
  color: ${props => props.theme.colors.textSecondary};
  letter-spacing: ${typography.letterSpacing.wide};
  font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase;
`;

const InfoValue = styled(Text).attrs(() => ({
  size: 'base' as const,
}))`
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.medium};
`;

const TextUppercase = styled(Text)`
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const SubsectionTitle = styled(Text)`
  margin: ${spacing[2]} 0 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
`;

const PendingBadge = styled.div`
  padding: ${spacing[2]} ${spacing[4]};
  background: ${props => props.theme.colors.warning}20;
  border: 1px solid ${props => props.theme.colors.warning};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
`;

type ProfileSection = 'personal' | 'professional';

const PROFILE_SECTION_IDS: ProfileSection[] = ['personal', 'professional'];

function parseSectionFromHash(): ProfileSection {
  const hash = window.location.hash.replace(/^#/, '').toLowerCase();
  // Legacy hashes from the old 4-tab layout land on Personal.
  if (hash === 'contact' || hash === 'membership') return 'personal';
  if (PROFILE_SECTION_IDS.includes(hash as ProfileSection)) return hash as ProfileSection;
  return 'personal';
}

const IndividualProfileView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: showError } = useToast();
  const watermark = useMonumentWatermark();
  const [member, setMember] = useState<IndividualMemberDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeSection, setActiveSection] = useState<ProfileSection>(() => parseSectionFromHash());

  useEffect(() => {
    loadMember();
    loadPendingChangeRequests();
  }, []);

  useEffect(() => {
    const syncFromHash = () => setActiveSection(parseSectionFromHash());
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  const handleTabChange = (tabId: string) => {
    const id = tabId as ProfileSection;
    if (!PROFILE_SECTION_IDS.includes(id)) return;
    setActiveSection(id);
    const url = `${window.location.pathname}${window.location.search}#${id}`;
    window.history.replaceState(null, '', url);
  };

  const loadMember = async () => {
    setLoading(true);
    try {
      const memberData = await memberService.getMyMember();
      setMember(memberData);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('profile.loadError')
      );
    } finally {
      setLoading(false);
    }
  };

  const loadPendingChangeRequests = async () => {
    try {
      const requests = await changeRequestService.getMyChangeRequests();
      const pending = requests.filter(r => r.status === ChangeRequestStatus.Pending);
      setPendingCount(pending.length);
    } catch {
      // Non-blocking
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const getGenderLabel = (gender?: Gender | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(Gender, gender);
    if (normalized == null) return '-';
    switch (normalized) {
      case Gender.Female: return t('profile.gender.female');
      case Gender.Male: return t('profile.gender.male');
      case Gender.Other: return t('profile.gender.other');
      case Gender.PreferNotToSay: return t('profile.gender.preferNotToSay');
      default: return '-';
    }
  };

  const getMembershipTypeLabel = (type?: MembershipType | string | number | null): string | null => {
    const labels: Record<MembershipType, string> = {
      [MembershipType.Regular]: t('membership.regular'),
      [MembershipType.Student]: t('membership.student'),
      [MembershipType.Retired]: t('membership.retired'),
      [MembershipType.Honorary]: t('membership.honorary'),
    };
    if (type == null || type === '') return null;
    if (typeof type === 'number' && labels[type as MembershipType]) {
      return labels[type as MembershipType];
    }
    if (typeof type === 'string') {
      const asNum = Number(type);
      if (!Number.isNaN(asNum) && labels[asNum as MembershipType]) {
        return labels[asNum as MembershipType];
      }
      const byName = MembershipType[type as keyof typeof MembershipType];
      if (typeof byName === 'number' && labels[byName]) {
        return labels[byName];
      }
    }
    return null;
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!member) {
    return (
      <ProfileContainer>
        <Stack direction="column" spacing="lg">
          <IdentityCard style={watermark.style}>
            <IdentityInner>
              <AccentBar aria-hidden />
              <Text size="base" weight="semibold" style={{ position: 'relative', zIndex: 2 }}>
                {t('profile.title')}
              </Text>
            </IdentityInner>
            <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
          </IdentityCard>
          <Card variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle" style={{ padding: spacing[8] }}>
            <Text size="base" color="secondary">
              {t('profile.notFound')}
            </Text>
            <Text size="sm" color="muted" style={{ marginTop: spacing[4] }}>
              {t('profile.notFoundHint')}
            </Text>
            <Button variant="outline" onClick={() => navigate('/')} style={{ marginTop: spacing[6] }}>
              {t('common.back')}
            </Button>
          </Card>
        </Stack>
      </ProfileContainer>
    );
  }

  const displayName = `${getMemberDisplayFirstName(member)} ${getMemberDisplayLastName(member)}`.trim();
  const parsedCard = parseMemberCardNumber(member.memberCardNumber);
  const parsedPreviousCard = parseMemberCardNumber(member.previousCardNumber);
  const membershipTypeLabel = getMembershipTypeLabel(member.membershipType);

  const sections: { id: ProfileSection; label: string }[] = [
    { id: 'personal', label: t('profile.sections.personal') },
    { id: 'professional', label: t('profile.sections.professional') },
  ];

  const bilingual = (en?: string | null, gr?: string | null) => {
    const parts = [en, gr].filter(Boolean);
    if (parts.length > 1) return `${en || '-'} / ${gr || '-'}`;
    return en || gr || null;
  };

  return (
    <ProfileContainer>
      <Stack direction="column" spacing="xl">
        <IdentityCard style={watermark.style}>
          <IdentityInner>
            <div style={{ width: '100%' }}>
              <AccentBar aria-hidden />
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: spacing[5],
                  width: '100%',
                }}
              >
            <IdentityMain>
              <Avatar name={displayName} size="xl" border borderColor="primary" />
              <IdentityText>
                <Name>{displayName}</Name>
                {member.email && (
                  <MetaRow>
                    <MetaItem>
                      <EnvelopeIcon aria-hidden />
                      {member.email}
                    </MetaItem>
                  </MetaRow>
                )}
                {membershipTypeLabel && <SoftBadge>{membershipTypeLabel}</SoftBadge>}
              </IdentityText>
            </IdentityMain>
            <EditProfileButton variant="outline" size="sm" onClick={() => navigate('/profile/edit')}>
              {t('profile.edit')}
            </EditProfileButton>
              </div>
            </div>
          </IdentityInner>
          <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
        </IdentityCard>

        {pendingCount > 0 && (
          <PendingBadge>
            {t('profile.pendingChangeRequests', { count: pendingCount }) || `You have ${pendingCount} pending change request(s) awaiting administrator approval.`}
          </PendingBadge>
        )}

        <Tabs
          activeTab={activeSection}
          onTabChange={handleTabChange}
          tabs={sections}
          aria-label={t('profile.sections.ariaLabel', 'Profile sections')}
        >
          <TabPanel tabId="personal">
            <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
              <Stack direction="column" spacing="lg">
                <SubsectionTitle>{t('profile.sections.identity')}</SubsectionTitle>
                <InfoGrid>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('common.firstName')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.name?.firstNameEn, member.name?.firstNameGr) || '-'}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.lastName')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.name?.lastNameEn, member.name?.lastNameGr) || '-'}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.genderLabel')}
                    </TextUppercase>
                    <Text size="base" weight="medium">{getGenderLabel(member.gender)}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.dateOfBirth')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {member.dateOfBirth ? formatDate(member.dateOfBirth) : <EmptyValue size="base">-</EmptyValue>}
                    </Text>
                  </InfoItem>
                </InfoGrid>

                <SubsectionTitle>{t('profile.sections.contactDetails')}</SubsectionTitle>
                <InfoGrid>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">{t('common.email')}</TextUppercase>
                    <Text size="base" weight="medium">{member.email ?? '-'}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">{t('common.phone')}</TextUppercase>
                    <Text size="base" weight="medium">{member.phone || <EmptyValue size="base">-</EmptyValue>}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">{t('common.mobile')}</TextUppercase>
                    <Text size="base" weight="medium">{member.mobile || <EmptyValue size="base">-</EmptyValue>}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.residentialAddress')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.residential?.addressEn, member.residential?.addressGr) || (
                        <EmptyValue size="base">-</EmptyValue>
                      )}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">{t('profile.residentialCity')}</TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.residential?.cityEn, member.residential?.cityGr) || (
                        <EmptyValue size="base">-</EmptyValue>
                      )}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.residentialPostalCode')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.residential?.postalCodeEn, member.residential?.postalCodeGr) || (
                        <EmptyValue size="base">-</EmptyValue>
                      )}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <TextUppercase size="xs" weight="semibold" color="secondary">
                      {t('profile.residentialCountry')}
                    </TextUppercase>
                    <Text size="base" weight="medium">
                      {bilingual(member.residential?.countryEn, member.residential?.countryGr) || (
                        <EmptyValue size="base">-</EmptyValue>
                      )}
                    </Text>
                  </InfoItem>
                </InfoGrid>

                <SubsectionTitle>{t('profile.sections.membershipDetails')}</SubsectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>{t('profile.membershipType')}</InfoLabel>
                    <InfoValue>{getMembershipTypeLabel(member.membershipType)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('profile.registrationYear')}</InfoLabel>
                    <InfoValue>{member.registrationYear}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('register.memberCardNumber')}</InfoLabel>
                    <InfoValue>{parsedCard.cardNumber || <EmptyValue>-</EmptyValue>}</InfoValue>
                  </InfoItem>
                  {parsedCard.firstRegisteredYear != null && (
                    <InfoItem>
                      <InfoLabel>{t('profile.firstRegisteredFromCard')}</InfoLabel>
                      <InfoValue>{parsedCard.firstRegisteredYear}</InfoValue>
                    </InfoItem>
                  )}
                  {(member.transferFromIcom || member.previousCardNumber) && (
                    <>
                      <InfoItem>
                        <InfoLabel>{t('register.transferFromIcom')}</InfoLabel>
                        <InfoValue>{member.transferFromIcom || <EmptyValue>-</EmptyValue>}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>{t('register.previousCardNumber')}</InfoLabel>
                        <InfoValue>
                          {parsedPreviousCard.cardNumber || member.previousCardNumber || (
                            <EmptyValue>-</EmptyValue>
                          )}
                        </InfoValue>
                      </InfoItem>
                    </>
                  )}
                  <InfoItem>
                    <InfoLabel>{t('profile.mainCommittee')}</InfoLabel>
                    <InfoValue>{member.mainCommittee || <EmptyValue>-</EmptyValue>}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('profile.additionalCommittees')}</InfoLabel>
                    <InfoValue>
                      {member.additionalCommittees && member.additionalCommittees.length > 0
                        ? member.additionalCommittees.join(', ')
                        : <EmptyValue>-</EmptyValue>}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('profile.isHonoraryMember')}</InfoLabel>
                    <InfoValue>{member.isHonoraryMember ? t('common.yes') : t('common.no')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('vignette.statusLabel')}</InfoLabel>
                    <VignetteStatusBadge status={resolveVignetteStatus(member)} />
                  </InfoItem>
                </InfoGrid>
              </Stack>
            </SectionCard>
          </TabPanel>
          <TabPanel tabId="professional">
            <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
              <ProfessionalProfileDisplay member={member} />
            </SectionCard>
          </TabPanel>
        </Tabs>
      </Stack>
    </ProfileContainer>
  );
};

export default IndividualProfileView;
